import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LogIn, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useLogin } from "../hooks/useLogin";
import { useLoginOtp } from "../hooks/useLoginOtp";
import { LoginFormData } from "../types/auth.types";

const loginSchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const otpEmailSchema = z.object({
  userId: z.string().email("Enter a valid email address"),
});

type OtpEmailFormData = z.infer<typeof otpEmailSchema>;
type LoginMode = "password" | "otp";

const OrDivider = () => (
  <div className="flex items-center gap-3 py-1">
    <span className="h-px flex-1 bg-border" />
    <span className="shrink-0 text-xs uppercase text-muted-foreground">or</span>
    <span className="h-px flex-1 bg-border" />
  </div>
);

export const LoginForm = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<LoginMode>("password");
  const [otpValue, setOtpValue] = useState("");
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  const { login, loading: passwordLoading, error: passwordError } = useLogin();
  const {
    step,
    userId: otpUserId,
    sending,
    verifying,
    error: otpError,
    resendSecondsLeft,
    canResend,
    sendOtp,
    resendOtp,
    verifyOtp,
    reset: resetOtp,
  } = useLoginOtp();

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    getValues: getPasswordValues,
    formState: { errors: passwordErrors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    reset: resetEmailForm,
  } = useForm<OtpEmailFormData>({
    resolver: zodResolver(otpEmailSchema),
    defaultValues: { userId: "" },
  });

  const { ref: emailFieldRef, ...emailFieldRegister } = registerEmail("userId");
  const codeSent = step === "otp";
  const otpComplete = otpValue.length === 6;

  useEffect(() => {
    if (mode !== "otp") return;
    requestAnimationFrame(() => {
      if (codeSent) {
        otpInputRef.current?.focus();
      } else {
        emailInputRef.current?.focus();
      }
    });
  }, [mode, codeSent]);

  const onPasswordSubmit = async (data: LoginFormData) => {
    const user = await login(data);
    if (user) {
      navigate("/dashboard");
    }
  };

  const handleSignInWithOtp = () => {
    const userId = getPasswordValues("userId")?.trim() ?? "";
    resetEmailForm({ userId });
    setOtpValue("");
    resetOtp();
    setMode("otp");
  };

  const onSendOtp = async (data: OtpEmailFormData) => {
    await sendOtp(data.userId.trim());
  };

  const handleOtpSignIn = async () => {
    if (!otpComplete || verifying || sending || !codeSent) return;

    const user = await verifyOtp(otpValue);
    if (user) {
      navigate("/dashboard");
    }
  };

  const handleOtpChange = (value: string) => {
    setOtpValue(value.replace(/\D/g, "").slice(0, 6));
  };

  const handleResend = async () => {
    if (!canResend) return;
    await resendOtp();
    setOtpValue("");
    otpInputRef.current?.focus();
  };

  const handleBackToSignIn = () => {
    resetEmailForm({ userId: "" });
    setOtpValue("");
    resetOtp();
    setMode("password");
  };

  const cardDescription =
    mode === "password"
      ? "Enter your credentials to access your account"
      : codeSent
        ? "Enter the OTP sent to your email"
        : "Enter your email to receive an OTP";

  return (
    <Card className="border-border shadow-medium">
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-xl">Sign in</CardTitle>
        <CardDescription>{cardDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        {mode === "password" ? (
          <form
            onSubmit={handlePasswordSubmit(onPasswordSubmit)}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="userId">User ID</Label>
              <Input
                id="userId"
                type="text"
                placeholder="Enter your user ID"
                autoComplete="username"
                {...registerPassword("userId")}
                className={
                  passwordErrors.userId
                    ? "border-destructive focus-visible:ring-destructive"
                    : ""
                }
              />
              {passwordErrors.userId && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {passwordErrors.userId.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/90 hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                {...registerPassword("password")}
                className={
                  passwordErrors.password
                    ? "border-destructive focus-visible:ring-destructive"
                    : ""
                }
              />
              {passwordErrors.password && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {passwordErrors.password.message}
                </p>
              )}
            </div>

            {passwordError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full h-11"
              loading={passwordLoading}
              loadingLabel="Signing in..."
            >
              <span className="flex items-center gap-2">
                <LogIn className="h-4 w-4" />
                Sign in
              </span>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              onClick={handleSignInWithOtp}
              disabled={passwordLoading || sending || verifying}
            >
              <span className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Sign in with OTP
              </span>
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-primary hover:text-primary/90 hover:underline font-medium"
              >
                Create account
              </Link>
            </p>
          </form>
        ) : !codeSent ? (
          <form
            onSubmit={handleEmailSubmit(onSendOtp)}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="otpEmail">Email</Label>
              <Input
                id="otpEmail"
                type="email"
                placeholder="Enter your email"
                autoComplete="email"
                {...emailFieldRegister}
                ref={(element) => {
                  emailFieldRef(element);
                  emailInputRef.current = element;
                }}
                className={
                  emailErrors.userId
                    ? "border-destructive focus-visible:ring-destructive"
                    : ""
                }
              />
              {emailErrors.userId && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {emailErrors.userId.message}
                </p>
              )}
            </div>

            {otpError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{otpError}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full h-11"
              loading={sending}
              loadingLabel="Sending OTP..."
            >
              <span className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Send OTP
              </span>
            </Button>

            <OrDivider />

            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              onClick={handleBackToSignIn}
              disabled={sending}
            >
              Back to sign in with password
            </Button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleOtpSignIn();
            }}
            className="space-y-5"
          >
            <p className="text-sm text-muted-foreground text-center">
              We have sent OTP to email id{" "}
              <span className="font-medium text-foreground">{otpUserId}</span>.
              It is valid for 10 minutes.
            </p>

            <div className="space-y-2">
              <Label htmlFor="otp">OTP</Label>
              <Input
                id="otp"
                ref={otpInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={otpValue}
                onChange={(e) => handleOtpChange(e.target.value)}
                disabled={sending || verifying}
                className="tracking-widest text-center text-lg"
              />
            </div>

            {otpError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{otpError}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full h-11"
              disabled={!otpComplete || sending || verifying}
              loading={verifying}
              loadingLabel="Signing in..."
            >
              <span className="flex items-center gap-2">
                <LogIn className="h-4 w-4" />
                Sign in
              </span>
            </Button>

            <div className="text-center">
              {sending ? (
                <p className="text-sm text-muted-foreground">Sending OTP...</p>
              ) : canResend ? (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={verifying}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Resend OTP
                </button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Resend in {resendSecondsLeft}s
                </p>
              )}
            </div>

            <OrDivider />

            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              onClick={handleBackToSignIn}
              disabled={sending || verifying}
            >
              Back to sign in with password
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};
